import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  type Time,
  type SeriesMarker,
} from 'lightweight-charts'
import PriceChartCore, {
  toTimestamp,
  currencySymbol,
  type OverlayLineSpec,
  type PriceLineSpec,
} from '@/components/ui/PriceChartCore'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  Bar,
  BarChart,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
} from 'recharts'
import { Loader2, Search, Plus, X, MessageSquare, Trash2 } from 'lucide-react'
import { clsx } from 'clsx'
import { getChartTechnicals, getChartIndicators, getChartCompare, getAnnotations, createAnnotation, deleteAnnotation as apiDeleteAnnotation, api } from '@/lib/api'
import type { TechnicalBar, IndicatorBar, CompareEntry, ChartAnnotation } from '@/lib/types'

// ── Config ──────────────────────────────────────────────────────────────────

type ChartMode = 'agentvault' | 'tradingview'

// ── TradingView ticker converter ────────────────────────────────────────────
// TradingView uses exchange-prefixed symbols: LSE:BATS, NASDAQ:MSFT, etc.

function toTradingViewSymbol(ticker: string): string {
  // Already formatted (e.g. "LSE:BATS") — pass through
  if (ticker.includes(':')) return ticker

  // Shanghai A-shares: 600276.SS → SSE:600276
  if (/^\d{6}\.SS$/i.test(ticker)) return `SSE:${ticker.split('.')[0]}`
  // Shenzhen: 000001.SZ → SZSE:000001
  if (/^\d{6}\.SZ$/i.test(ticker)) return `SZSE:${ticker.split('.')[0]}`

  // UK London Stock Exchange suffixes
  if (ticker.endsWith('.L')) return `LSE:${ticker.slice(0, -2)}`
  // LSE AIM
  if (ticker.endsWith('.LN')) return `LSE:${ticker.slice(0, -3)}`

  // Known US tickers → exchange mapping (best-effort)
  const NASDAQ_TICKERS = new Set([
    'AAPL', 'MSFT', 'NVDA', 'GOOGL', 'GOOG', 'AMZN', 'META', 'TSLA', 'AVGO',
    'COST', 'ASML', 'NFLX', 'AMD', 'INTC', 'QCOM', 'TXN', 'AMAT', 'MU',
    'KLAC', 'LRCX', 'MRVL', 'ADI', 'CDNS', 'SNPS', 'FTNT', 'PANW', 'CRWD',
  ])
  const NYSE_TICKERS = new Set([
    'BRK.B', 'JPM', 'V', 'MA', 'UNH', 'XOM', 'JNJ', 'WMT', 'PG', 'HD',
    'CVX', 'MRK', 'LLY', 'ABBV', 'PEP', 'KO', 'BAC', 'WFC', 'GS', 'MS',
    'GE', 'CAT', 'DE', 'UPS', 'RTX', 'HON', 'MMM', 'IBM', 'BA',
    'BATS', 'LGEN', 'RIO', 'SHEL', 'BP', 'AZN', 'GSK', 'HSBA', 'LLOY',
    'VOD', 'BT.A', 'PRU', 'NG.', 'SSE', 'SGE', 'WPP',
  ])

  // Crypto pairs: BTC-USD, ETH-USD, etc.
  if (ticker.includes('-USD') || ticker.includes('-USDT')) {
    const base = ticker.split('-')[0]
    return `BINANCE:${base}USDT`
  }

  if (NASDAQ_TICKERS.has(ticker)) return `NASDAQ:${ticker}`
  if (NYSE_TICKERS.has(ticker)) return `NYSE:${ticker}`

  // Taiwan TWSE main board: 2330.TW → TWSE:2330
  if (/^\d{4}\.TW$/i.test(ticker)) return `TWSE:${ticker.split('.')[0]}`
  // Taiwan OTC (Taipei Exchange): 6547.TWO → TPEX:6547
  if (/^\d{4}\.TWO$/i.test(ticker)) return `TPEX:${ticker.split('.')[0]}`

  // Fallback: let TradingView auto-resolve (works for most US equities)
  return ticker
}

const PERIODS = ['1D', '5D', '1M', '3M', '6M', '1Y', '2Y', '5Y'] as const
const INTERVALS = ['1m', '5m', '15m', '1H', '1D', '1W', '1M'] as const
const QUICK_TICKERS = ['AAPL', 'MSFT', 'NVDA', 'GOOGL', 'TSLA']

const OVERLAY_OPTIONS = [
  { key: 'sma20', label: 'SMA 20', color: '#3b82f6' },
  { key: 'sma50', label: 'SMA 50', color: '#f59e0b' },
  { key: 'sma200', label: 'SMA 200', color: '#ef4444' },
  { key: 'bb', label: 'Bollinger', color: '#8b5cf6' },
] as const

const INDICATOR_OPTIONS = [
  { key: 'rsi', label: 'RSI' },
  { key: 'macd', label: 'MACD' },
  { key: 'stoch', label: 'Stochastic' },
  { key: 'volume', label: 'Volume' },
] as const

type OverlayKey = (typeof OVERLAY_OPTIONS)[number]['key']
type IndicatorKey = (typeof INDICATOR_OPTIONS)[number]['key']

const COMPARE_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16']

// Map display intervals to API values
function apiInterval(iv: string): string {
  const map: Record<string, string> = { '1H': '1h', '1D': '1d', '1W': '1wk', '1M': '1mo' }
  return map[iv] ?? iv
}

function apiPeriod(p: string): string {
  const map: Record<string, string> = { '1D': '1d', '5D': '5d', '1M': '1m', '3M': '3m', '6M': '6m', '1Y': '1y', '2Y': '2y', '5Y': '5y' }
  return map[p] ?? p.toLowerCase()
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// toTimestamp + currencySymbol now live in PriceChartCore (single source of
// truth for both the modal and this page) and are imported above.

function generateId(): string {
  return Math.random().toString(36).slice(2, 11)
}

/** Read a persisted boolean chart-toggle pref. Storage access is guarded so
 *  private-mode / SSR never throws; falls back to `fallback` when unset. */
function readBoolPref(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key)
    if (v === '1') return true
    if (v === '0') return false
  } catch { /* storage unavailable */ }
  return fallback
}

// ── Annotation helpers ──────────────────────────────────────────────────────

function loadAnnotations(ticker: string): ChartAnnotation[] {
  try {
    const raw = localStorage.getItem(`chart_annotations_${ticker}`)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveAnnotations(ticker: string, annotations: ChartAnnotation[]) {
  localStorage.setItem(`chart_annotations_${ticker}`, JSON.stringify(annotations))
}

// ── Component ──────────────────────────────────────────────────────────────

export default function ChartPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  // Accept both ?ticker= and ?symbol= — the command palette navigates with
  // ?symbol=, while other callers use ?ticker=. Reading only one meant a
  // searched code (e.g. VWRL via ?symbol=) was ignored and the chart fell
  // back to AAPL, which the user then had to override every time.
  const initialTicker = searchParams.get('ticker') || searchParams.get('symbol') || 'AAPL'

  const [ticker, setTicker] = useState(initialTicker)
  const [inputValue, setInputValue] = useState(initialTicker)
  const [period, setPeriod] = useState<string>('1Y')
  const [interval, setInterval] = useState<string>('1D')
  const [chartType, setChartType] = useState<'candle' | 'line'>('candle')
  const [overlays, setOverlays] = useState<Set<OverlayKey>>(new Set())
  const [indicators, setIndicators] = useState<Set<IndicatorKey>>(new Set(['volume']))
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [bars, setBars] = useState<TechnicalBar[]>([])
  const [indicatorBars, setIndicatorBars] = useState<IndicatorBar[]>([])
  const [currency, setCurrency] = useState<string>('USD')
  // Track last fetch key to debounce rapid re-fetches
  const lastFetchRef = useRef<{ key: string; at: number }>({ key: '', at: 0 })
  const activeReqRef = useRef(0)
  const [crosshairData, setCrosshairData] = useState<TechnicalBar | null>(null)

  // Compare mode
  const [compareMode, setCompareMode] = useState(false)
  const [compareTickers, setCompareTickers] = useState<string[]>([])
  const [compareInput, setCompareInput] = useState('')
  const [compareData, setCompareData] = useState<CompareEntry[]>([])
  const [compareTickerList, setCompareTickerList] = useState<string[]>([])
  const [compareLoading, setCompareLoading] = useState(false)

  // Annotations
  const [annotations, setAnnotations] = useState<ChartAnnotation[]>([])
  const [annotationMode, setAnnotationMode] = useState(false)
  const [annotationInput, setAnnotationInput] = useState('')
  const [pendingAnnotation, setPendingAnnotation] = useState<{ date: string; price: number } | null>(null)

  const [chartMode, setChartMode] = useState<ChartMode>('agentvault')

  // Signal + SMC overlays — plot signal history and Smart Money Concepts
  // structure (FVGs, order blocks, BOS/CHoCH) directly on the price chart.
  const [showSignals, setShowSignals] = useState(() => readBoolPref('fg.chart.showSignals', true))
  const [showSmc, setShowSmc] = useState(() => readBoolPref('fg.chart.showSmc', false))
  const [signalEvents, setSignalEvents] = useState<{ created_at: string; source: string; signal_type: string; sentiment: number | null; summary: string }[]>([])
  // Optional click-to-filter on the per-source breakdown chips (null = show all).
  const [sourceFilter, setSourceFilter] = useState<string | null>(null)
  const [smc, setSmc] = useState<{
    structure_trend?: string
    events?: { type: string; dir: string; level: number }[]
    fair_value_gaps?: { dir: string; top: number; bottom: number; mitigated: boolean }[]
    order_blocks?: { dir: string; top: number; bottom: number }[]
    liquidity_sweeps?: { dir: string; level: number }[]
  } | null>(null)

  // Unified trend read for this ticker (structure+momentum+velocity+accel).
  const [trend, setTrend] = useState<{
    has_trend?: boolean
    trend_score?: number
    strength?: number
    direction?: string
    acceleration?: string
    headline?: string
    components?: Record<string, { score: number | null }>
    divergence?: { kind: string; lean: 'bullish' | 'bearish'; gap: number; note: string } | null
  } | null>(null)

  // Signal-agreement intel for this ticker (how cleanly directional signals concur).
  const [tickerIntel, setTickerIntel] = useState<{
    agreement_ratio?: number | null
    directional_count?: number | null
  } | null>(null)

  // Price-trend ribbon over the lookback (structure+momentum rolling score).
  const [trendSeries, setTrendSeries] = useState<{ date: string; score: number }[]>([])

  // Your trade levels (entry/stop/target) for this ticker from the blotter.
  const [showTrades, setShowTrades] = useState(() => readBoolPref('fg.chart.showTrades', false))
  const [tradeLevels, setTradeLevels] = useState<{ entry_price?: number | null; stop_price?: number | null; target_price?: number | null; acted_price?: number | null; direction?: string; status?: string }[]>([])

  // Persist the overlay toggles so they survive navigating between tickers/reloads.
  useEffect(() => { try { localStorage.setItem('fg.chart.showSignals', showSignals ? '1' : '0') } catch { /* ignore */ } }, [showSignals])
  useEffect(() => { try { localStorage.setItem('fg.chart.showSmc', showSmc ? '1' : '0') } catch { /* ignore */ } }, [showSmc])
  useEffect(() => { try { localStorage.setItem('fg.chart.showTrades', showTrades ? '1' : '0') } catch { /* ignore */ } }, [showTrades])

  // Load annotations when ticker changes — API-first, localStorage fallback
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const fetched = await getAnnotations(ticker)
        if (!cancelled) setAnnotations(fetched)
      } catch {
        // Offline / unauthenticated — fall back to localStorage
        if (!cancelled) setAnnotations(loadAnnotations(ticker))
      }
    }
    load()
    return () => { cancelled = true }
  }, [ticker])

  // Fetch signal history + SMC structure for the price overlays.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await api.get(`/api/signals/ticker/${encodeURIComponent(ticker)}/history`, { params: { days: 90 } })
        if (!cancelled) setSignalEvents(r.data?.signals ?? [])
      } catch { if (!cancelled) setSignalEvents([]) }
      try {
        const r = await api.get(`/api/charts/smc/${encodeURIComponent(ticker)}`, { params: { period: '6mo' } })
        if (!cancelled) setSmc(r.data?.has_structure ? r.data : null)
      } catch { if (!cancelled) setSmc(null) }
      try {
        const r = await api.get('/api/blotter/ideas', { params: { ticker, status: 'all', limit: 20 } })
        if (!cancelled) setTradeLevels(r.data?.ideas ?? [])
      } catch { if (!cancelled) setTradeLevels([]) }
      try {
        const r = await api.get(`/api/trends/${encodeURIComponent(ticker)}`)
        if (!cancelled) setTrend(r.data?.has_trend ? r.data : null)
      } catch { if (!cancelled) setTrend(null) }
      try {
        const r = await api.get(`/api/trends/${encodeURIComponent(ticker)}/series`, { params: { points: 50 } })
        if (!cancelled) setTrendSeries(r.data?.has_series ? (r.data.points ?? []) : [])
      } catch { if (!cancelled) setTrendSeries([]) }
      try {
        const r = await api.get(`/api/signals/ticker/${encodeURIComponent(ticker)}`)
        if (!cancelled) setTickerIntel({
          agreement_ratio: r.data?.agreement_ratio ?? null,
          directional_count: r.data?.directional_count ?? null,
        })
      } catch { if (!cancelled) setTickerIntel(null) }
    })()
    return () => { cancelled = true }
  }, [ticker])

  // ── Fetch data ──────────────────────────────────────────────────────────

  const fetchData = useCallback(async (t: string, p: string, iv: string) => {
    const fetchKey = `${t}:${p}:${iv}`
    const now = Date.now()
    const { key: lastKey, at: lastAt } = lastFetchRef.current

    // Debounce: skip if the exact same request completed less than 30s ago
    if (fetchKey === lastKey && now - lastAt < 30_000) {
      return
    }

    const hasStaleData = bars.length > 0
    if (hasStaleData) {
      // Background refresh — keep existing chart, show "refreshing..." instead of spinner
      setRefreshing(true)
      setError(null)
    } else {
      setLoading(true)
      setError(null)
    }

    // Race guard: switching A->B quickly could let the SLOWER A response land
    // last and overwrite the chart with the wrong ticker's candles. Stamp each
    // request and only commit the latest one's results.
    const reqId = ++activeReqRef.current
    try {
      const [techResp, indResp] = await Promise.all([
        getChartTechnicals(t, apiPeriod(p), apiInterval(iv)),
        getChartIndicators(t, apiPeriod(p)),
      ])
      if (reqId !== activeReqRef.current) return  // a newer request superseded us
      setBars(techResp.data)
      setIndicatorBars(indResp.data)
      if (techResp.currency) setCurrency(techResp.currency)
      lastFetchRef.current = { key: fetchKey, at: Date.now() }
    } catch (err: unknown) {
      if (reqId !== activeReqRef.current) return
      const msg = err instanceof Error ? err.message : 'Failed to load chart data'
      setError(msg)
      // Do NOT clear bars/indicatorBars — keep stale data visible
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [bars.length])

  // Fetch compare data
  const fetchCompare = useCallback(async (tickers: string[], p: string) => {
    if (tickers.length === 0) {
      setCompareData([])
      setCompareTickerList([])
      return
    }
    setCompareLoading(true)
    try {
      const resp = await getChartCompare(tickers, apiPeriod(p))
      setCompareData(resp.data)
      setCompareTickerList(resp.tickers)
    } catch {
      setCompareData([])
      setCompareTickerList([])
    } finally {
      setCompareLoading(false)
    }
  }, [])

  // Initial load + when ticker/period/interval change
  useEffect(() => {
    fetchData(ticker, period, interval)
  }, [ticker, period, interval, fetchData])

  // Refetch compare when tickers or period changes
  useEffect(() => {
    if (compareMode && compareTickers.length > 0) {
      fetchCompare([ticker, ...compareTickers], period)
    }
  }, [compareMode, compareTickers, ticker, period, fetchCompare])

  // Sync INCOMING url param → state. When the user searches a new code while
  // already on the chart page, the palette navigates with ?symbol=NEW; without
  // this, the mount-only read would ignore it and the URL-writer below would
  // stomp it back to the old ticker (the "always launches AAPL" bug).
  useEffect(() => {
    const incoming = searchParams.get('ticker') || searchParams.get('symbol')
    if (incoming && incoming.toUpperCase() !== ticker.toUpperCase()) {
      setTicker(incoming)
      setInputValue(incoming)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // Update URL when ticker changes (canonicalise to ?ticker=)
  useEffect(() => {
    const current = searchParams.get('ticker')
    if (current?.toUpperCase() !== ticker.toUpperCase()) {
      setSearchParams({ ticker }, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker])

  // ── Chart rendering is now the shared PriceChartCore (see JSX below). The
  // old ~300-line inline lightweight-charts builder lived here; it's been
  // extracted so the modal and this page can never drift again. The prop
  // builders (coreOverlays / coreMarkers / corePriceLines) are defined below.

  // ── Recharts data for sub-panels ────────────────────────────────────────

  // Signal density: a burst of signals in a short window is an early trend tell.
  // Compare the last 7 days' signal count to the prior 30-day daily average.
  const signalDensity = useMemo(() => {
    if (!signalEvents.length) return null
    const now = Date.now()
    const day = 86_400_000
    let last7 = 0, prior = 0
    let bull = 0, bear = 0
    for (const s of signalEvents) {
      const t = new Date(s.created_at || '').getTime()
      if (!Number.isFinite(t)) continue
      const ageDays = (now - t) / day
      if (ageDays <= 7) {
        last7++
        const sent = s.sentiment ?? 0
        if (sent > 0.1) bull++
        else if (sent < -0.1) bear++
      } else if (ageDays <= 37) {
        prior++
      }
    }
    const priorDaily = prior / 30
    const last7Daily = last7 / 7
    // "Cluster" when recent daily rate is ≥2× the baseline and we have ≥3 signals.
    const ratio = priorDaily > 0 ? last7Daily / priorDaily : (last7 >= 3 ? 3 : 1)
    return {
      last7, ratio: Math.round(ratio * 10) / 10,
      cluster: last7 >= 3 && ratio >= 2,
      lean: bull > bear ? 'bullish' : bear > bull ? 'bearish' : 'mixed',
    }
  }, [signalEvents])

  // 30-day signal-density heatstrip: one cell per calendar day (oldest→newest).
  // Intensity = count vs busiest day; colour = net sentiment lean for that day.
  const signalHeat = useMemo(() => {
    if (!signalEvents.length) return null
    const day = 86_400_000
    const now = Date.now()
    // Anchor "today" to local midnight so each cell maps to a calendar day.
    const todayMid = new Date(now)
    todayMid.setHours(0, 0, 0, 0)
    const todayMs = todayMid.getTime()
    // Build 30 day buckets keyed by 'YYYY-MM-DD', oldest→newest (index 0 = 29d ago).
    const days: { date: string; count: number; net: number }[] = []
    const idxByDate = new Map<string, number>()
    for (let i = 29; i >= 0; i--) {
      const d = new Date(todayMs - i * day)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      idxByDate.set(key, days.length)
      days.push({ date: key, count: 0, net: 0 })
    }
    for (const s of signalEvents) {
      const t = new Date(s.created_at || '').getTime()
      if (!Number.isFinite(t)) continue
      const d = new Date(t)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      const idx = idxByDate.get(key)
      if (idx == null) continue
      days[idx].count += 1
      days[idx].net += s.sentiment ?? 0
    }
    let maxCount = 0
    for (const d of days) if (d.count > maxCount) maxCount = d.count
    return { days, maxCount }
  }, [signalEvents])

  // Signal breakdown by source: which feeds are firing on this ticker, and
  // whether each leans bullish/bearish (net sentiment). Top 8 by count.
  const signalsBySource = useMemo(() => {
    if (!signalEvents.length) return null
    const agg = new Map<string, { count: number; net: number }>()
    for (const s of signalEvents) {
      const src = s.source || 'unknown'
      const cur = agg.get(src) ?? { count: 0, net: 0 }
      cur.count += 1
      cur.net += s.sentiment ?? 0
      agg.set(src, cur)
    }
    const all = Array.from(agg.entries()).map(([source, v]) => ({
      source,
      count: v.count,
      net: v.net,
      lean: v.net > 0.1 ? 'bullish' : v.net < -0.1 ? 'bearish' : 'neutral',
    }))
    all.sort((a, b) => b.count - a.count)
    const totalSources = all.length
    return { rows: all.slice(0, 8), totalSources }
  }, [signalEvents])

  const rechartsData = useMemo(() => {
    return indicatorBars.map(b => ({
      date: b.date,
      rsi: b.rsi,
      macd: b.macd,
      macd_signal: b.macd_signal,
      macd_histogram: b.macd_histogram,
      stoch_k: b.stoch_k,
      stoch_d: b.stoch_d,
      close: b.close,
      volume: b.volume,
      ob_os: b.ob_os,
    }))
  }, [indicatorBars])

  // ── PriceChartCore prop builders ──────────────────────────────────────────
  // These reproduce exactly what the old inline chart-building useEffect drew,
  // but as declarative data the shared PriceChartCore renders. Same markers,
  // overlays and price-lines as before — no visual change, just one renderer.

  // Overlay lines (SMA 20/50/200, Bollinger bands) driven by the overlay toggles.
  const coreOverlays = useMemo<OverlayLineSpec[]>(() => {
    const out: OverlayLineSpec[] = []
    if (overlays.has('sma20')) out.push({ key: 'sma20', color: '#3b82f6', label: 'SMA 20', values: bars.map(b => b.sma_20) })
    if (overlays.has('sma50')) out.push({ key: 'sma50', color: '#f59e0b', label: 'SMA 50', values: bars.map(b => b.sma_50) })
    if (overlays.has('sma200')) out.push({ key: 'sma200', color: '#ef4444', label: 'SMA 200', values: bars.map(b => b.sma_200) })
    if (overlays.has('bb')) {
      out.push({ key: 'bb_upper', color: '#8b5cf6', label: 'BB Upper', values: bars.map(b => b.bb_upper) })
      out.push({ key: 'bb_middle', color: '#a78bfa', label: 'BB Mid', values: bars.map(b => b.bb_middle) })
      out.push({ key: 'bb_lower', color: '#8b5cf6', label: 'BB Lower', values: bars.map(b => b.bb_lower) })
    }
    return out
  }, [overlays, bars])

  // Price markers: OB/OS arrows, annotation flags, and (if toggled) signal
  // history coloured by sentiment.
  const coreMarkers = useMemo<SeriesMarker<Time>[]>(() => {
    const markers: SeriesMarker<Time>[] = []
    for (const ib of indicatorBars) {
      if (ib.ob_os === 'overbought') {
        markers.push({ time: toTimestamp(ib.date), position: 'aboveBar', color: '#ef4444', shape: 'arrowDown', text: 'OB' })
      } else if (ib.ob_os === 'oversold') {
        markers.push({ time: toTimestamp(ib.date), position: 'belowBar', color: '#10b981', shape: 'arrowUp', text: 'OS' })
      }
    }
    for (const ann of annotations) {
      markers.push({ time: toTimestamp(ann.date), position: 'aboveBar', color: '#f59e0b', shape: 'arrowDown', text: ann.note.slice(0, 10) })
    }
    if (showSignals) {
      for (const sig of signalEvents) {
        const sent = sig.sentiment ?? 0
        const bull = sent > 0.1
        const bear = sent < -0.1
        markers.push({
          time: toTimestamp((sig.created_at || '').slice(0, 10)),
          position: bear ? 'aboveBar' : 'belowBar',
          color: bull ? '#22c55e' : bear ? '#f43f5e' : '#94a3b8',
          shape: bull ? 'arrowUp' : bear ? 'arrowDown' : 'circle',
          text: (sig.source || '').replace(/_/g, ' ').slice(0, 14),
        })
      }
    }
    return markers
  }, [indicatorBars, annotations, showSignals, signalEvents])

  // Horizontal price lines: SMC structure (BOS/CHoCH, FVG, sweeps, order blocks)
  // and your blotter trade levels (entry / stop / target).
  const corePriceLines = useMemo<PriceLineSpec[]>(() => {
    const lines: PriceLineSpec[] = []
    if (showSmc && smc) {
      for (const e of (smc.events ?? []).slice(-3)) {
        lines.push({ price: e.level, color: e.dir === 'bullish' ? '#22c55e' : '#f43f5e', title: `${e.type}` })
      }
      const openFvg = (smc.fair_value_gaps ?? []).find(f => !f.mitigated)
      if (openFvg) {
        lines.push({ price: openFvg.top, color: '#f59e0b', title: 'FVG', dashed: true })
        lines.push({ price: openFvg.bottom, color: '#f59e0b', title: '', dashed: true })
      }
      for (const s of (smc.liquidity_sweeps ?? []).slice(0, 2)) {
        lines.push({ price: s.level, color: '#60a5fa', title: 'sweep', dashed: true })
      }
      for (const ob of (smc.order_blocks ?? []).slice(-2)) {
        const c = ob.dir === 'bullish' ? '#16a34a' : '#e11d48'
        lines.push({ price: ob.top, color: c, title: ob.dir === 'bullish' ? 'demand OB' : 'supply OB' })
        lines.push({ price: ob.bottom, color: c, title: '' })
      }
    }
    if (showTrades) {
      for (const t of tradeLevels.filter(t => t.status !== 'dismissed' && t.status !== 'closed').slice(0, 3)) {
        const entry = t.acted_price ?? t.entry_price
        if (entry != null) lines.push({ price: entry, color: '#3b82f6', title: 'ENTRY', width: 2 })
        if (t.stop_price != null) lines.push({ price: t.stop_price, color: '#ef4444', title: 'STOP', width: 2 })
        if (t.target_price != null) lines.push({ price: t.target_price, color: '#22c55e', title: 'TARGET', width: 2 })
      }
    }
    return lines
  }, [showSmc, smc, showTrades, tradeLevels])

  const coreTimeVisible = interval === '1m' || interval === '5m' || interval === '15m' || interval === '1H'

  // Annotation-mode click: place a pending note at the clicked bar.
  const handleBarClick = useCallback((bar: TechnicalBar) => {
    if (annotationMode) setPendingAnnotation({ date: bar.date, price: bar.close })
  }, [annotationMode])

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleTickerSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const v = inputValue.trim().toUpperCase()
    if (v) setTicker(v)
  }

  const toggleOverlay = (key: OverlayKey) => {
    setOverlays(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleIndicator = (key: IndicatorKey) => {
    setIndicators(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const addCompareTicker = () => {
    const t = compareInput.trim().toUpperCase()
    if (t && !compareTickers.includes(t) && t !== ticker) {
      setCompareTickers(prev => [...prev, t])
      setCompareInput('')
    }
  }

  const removeCompareTicker = (t: string) => {
    setCompareTickers(prev => prev.filter(x => x !== t))
  }

  const addAnnotation = async () => {
    if (!pendingAnnotation || !annotationInput.trim()) return
    const ann: ChartAnnotation = {
      id: generateId(),
      date: pendingAnnotation.date,
      price: pendingAnnotation.price,
      note: annotationInput.trim(),
      createdAt: new Date().toISOString(),
    }
    const updated = [...annotations, ann]
    setAnnotations(updated)
    setPendingAnnotation(null)
    setAnnotationInput('')
    setAnnotationMode(false)
    try {
      await createAnnotation(ticker, { id: ann.id, date: ann.date, price: ann.price, note: ann.note })
    } catch {
      // Persist locally as fallback when the API is unavailable
      saveAnnotations(ticker, updated)
    }
  }

  const deleteAnnotation = async (id: string) => {
    const updated = annotations.filter(a => a.id !== id)
    setAnnotations(updated)
    try {
      await apiDeleteAnnotation(ticker, id)
    } catch {
      // Reflect deletion in localStorage too when offline
      saveAnnotations(ticker, updated)
    }
  }

  // Price info for header
  const lastBar = bars.length > 0 ? bars[bars.length - 1] : null
  const firstBar = bars.length > 0 ? bars[0] : null
  const priceChange = lastBar && firstBar ? lastBar.close - firstBar.close : 0
  const priceChangePct = firstBar && firstBar.close ? (priceChange / firstBar.close) * 100 : 0

  const displayBar = crosshairData || lastBar

  // Format date for recharts x-axis
  const formatDate = (d: string) => {
    if (!d) return ''
    return d.length > 10 ? d.slice(5, 10) : d.slice(5)
  }

  return (
    <div className="flex flex-col h-full bg-base overflow-auto">
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="shrink-0 glass sticky top-0 z-20 px-4 py-3 border-b border-border space-y-2">
        {/* Row 1: Ticker input + quick tickers */}
        <div className="flex items-center gap-3 flex-wrap">
          <form onSubmit={handleTickerSubmit} className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value.toUpperCase())}
                placeholder="Ticker..."
                className="input w-28 pl-8 pr-3 py-1.5 font-bold"
              />
            </div>
            <button
              type="submit"
              className="btn-primary px-3 py-1.5 text-xs font-semibold"
            >
              Go
            </button>
          </form>

          <div className="flex items-center gap-1">
            {QUICK_TICKERS.map(t => (
              <button
                type="button"
                key={t}
                onClick={() => { setInputValue(t); setTicker(t) }}
                className={clsx(
                  'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                  ticker === t
                    ? 'bg-blue/20 text-blue-light'
                    : 'text-text-secondary hover:bg-surface-card hover:text-text-primary',
                )}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Price info */}
          {lastBar && (
            <div className="ml-auto flex items-center gap-4">
              <span className="text-lg font-bold text-text-primary">{ticker}</span>
              <span className="text-lg font-mono text-text-primary">{currencySymbol(currency)}{lastBar.close.toFixed(2)}</span>
              <span className={clsx(
                'font-mono',
                priceChange >= 0 ? 'delta-chip-pos' : 'delta-chip-neg',
              )}>
                {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)} ({priceChangePct >= 0 ? '+' : ''}{priceChangePct.toFixed(2)}%)
              </span>
            </div>
          )}
        </div>

        {/* Row 2: Chart Type + Period + Interval + Overlays */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Chart type toggle */}
          <div className="flex items-center gap-0.5 bg-surface-DEFAULT rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setChartType('candle')}
              className={clsx(
                'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                chartType === 'candle' ? 'bg-blue text-white' : 'text-text-secondary hover:text-text-primary',
              )}
            >
              Candle
            </button>
            <button
              type="button"
              onClick={() => setChartType('line')}
              className={clsx(
                'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                chartType === 'line' ? 'bg-blue text-white' : 'text-text-secondary hover:text-text-primary',
              )}
            >
              Line
            </button>
          </div>

          {/* Period */}
          <div className="flex items-center gap-0.5 bg-surface-DEFAULT rounded-lg p-0.5">
            {PERIODS.map(p => (
              <button
                type="button"
                key={p}
                onClick={() => setPeriod(p)}
                className={clsx(
                  'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                  period === p
                    ? 'bg-blue text-white'
                    : 'text-text-secondary hover:text-text-primary',
                )}
              >
                {p}
              </button>
            ))}
          </div>

          {/* Interval */}
          <div className="flex items-center gap-0.5 bg-surface-DEFAULT rounded-lg p-0.5">
            {INTERVALS.map(iv => (
              <button
                type="button"
                key={iv}
                onClick={() => setInterval(iv)}
                className={clsx(
                  'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                  interval === iv
                    ? 'bg-purple text-white'
                    : 'text-text-secondary hover:text-text-primary',
                )}
              >
                {iv}
              </button>
            ))}
          </div>

          {/* Overlays — only relevant for AgentVault chart */}
          {chartMode === 'agentvault' && (
            <div className="flex items-center gap-1">
              {OVERLAY_OPTIONS.map(o => (
                <button
                  type="button"
                  key={o.key}
                  onClick={() => toggleOverlay(o.key)}
                  className={clsx(
                    'px-2.5 py-1 rounded text-xs font-medium transition-colors border',
                    overlays.has(o.key)
                      ? `border-current overlay-active-${o.key}`
                      : 'border-transparent text-text-secondary hover:text-text-primary',
                  )}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}

          {/* Chart mode toggle */}
          <div className="ml-auto flex items-center gap-0.5 bg-surface-DEFAULT rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setChartMode('agentvault')}
              className={clsx(
                'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                chartMode === 'agentvault'
                  ? 'bg-blue text-white'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              AgentVault Chart
            </button>
            <button
              type="button"
              onClick={() => setChartMode('tradingview')}
              className={clsx(
                'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                chartMode === 'tradingview'
                  ? 'bg-blue text-white'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              TradingView Chart
            </button>
          </div>
        </div>

        {/* Row 3: Indicator toggles + Compare + Annotate — AgentVault mode only */}
        {chartMode === 'agentvault' && (<>
        <div className="flex items-center gap-4 flex-wrap">
          {/* Indicator checkboxes */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-muted font-medium">Indicators:</span>
            {INDICATOR_OPTIONS.map(ind => (
              <label key={ind.key} className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={indicators.has(ind.key)}
                  onChange={() => toggleIndicator(ind.key)}
                  className="w-3 h-3 rounded border-border accent-blue"
                />
                <span className={clsx(
                  'text-xs font-medium',
                  indicators.has(ind.key) ? 'text-text-primary' : 'text-text-secondary',
                )}>
                  {ind.label}
                </span>
              </label>
            ))}
          </div>

          {/* Signal + SMC overlay toggles */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-muted font-medium">Overlays:</span>
            <button
              type="button"
              onClick={() => setShowSignals(v => !v)}
              className={clsx('text-xs px-2 py-0.5 rounded border',
                showSignals ? 'border-emerald/50 text-emerald-light bg-emerald/10' : 'border-border text-text-secondary')}
              title="Plot signal history on the chart, coloured by sentiment"
            >
              📡 Signals{signalEvents.length ? ` (${signalEvents.length})` : ''}
            </button>
            <button
              type="button"
              onClick={() => setShowSmc(v => !v)}
              className={clsx('text-xs px-2 py-0.5 rounded border',
                showSmc ? 'border-amber/50 text-amber-light bg-amber/10' : 'border-border text-text-secondary')}
              title="Smart Money Concepts structure: BOS/CHoCH levels, fair value gaps, liquidity sweeps"
            >
              📐 SMC{smc?.structure_trend ? ` · ${smc.structure_trend}` : ''}
            </button>
            <button
              type="button"
              onClick={() => setShowTrades(v => !v)}
              className={clsx('text-xs px-2 py-0.5 rounded border',
                showTrades ? 'border-blue/50 text-blue-light bg-blue/10' : 'border-border text-text-secondary')}
              title="Your blotter trade levels for this ticker: entry / stop / target"
            >
              🎯 My Trades{tradeLevels.length ? ` (${tradeLevels.length})` : ''}
            </button>
          </div>

          {/* Compare button */}
          <button
            type="button"
            onClick={() => {
              setCompareMode(!compareMode)
              if (compareMode) {
                setCompareTickers([])
                setCompareData([])
                setCompareTickerList([])
              }
            }}
            className={clsx(
              'px-3 py-1 rounded-lg text-xs font-semibold transition-colors border',
              compareMode
                ? 'bg-blue/20 text-blue-light border-blue'
                : 'border-border text-text-secondary hover:text-text-primary hover:border-text-muted',
            )}
          >
            Compare
          </button>

          {/* Annotate button */}
          <button
            type="button"
            onClick={() => {
              setAnnotationMode(!annotationMode)
              setPendingAnnotation(null)
              setAnnotationInput('')
            }}
            className={clsx(
              'px-3 py-1 rounded-lg text-xs font-semibold transition-colors border flex items-center gap-1',
              annotationMode
                ? 'bg-amber/20 text-amber-light border-amber'
                : 'border-border text-text-secondary hover:text-text-primary hover:border-text-muted',
            )}
          >
            <MessageSquare size={12} />
            Add Note
          </button>
        </div>

        {/* Compare mode input */}
        {compareMode && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-text-muted">Compare with:</span>
            <form
              onSubmit={e => { e.preventDefault(); addCompareTicker() }}
              className="flex items-center gap-1"
            >
              <input
                type="text"
                value={compareInput}
                onChange={e => setCompareInput(e.target.value.toUpperCase())}
                placeholder="MSFT..."
                className="input w-20 px-2 py-1 text-xs rounded"
              />
              <button
                type="submit"
                title="Add ticker"
                className="p-1 rounded bg-blue/20 text-blue-light hover:bg-blue/30"
              >
                <Plus size={12} />
              </button>
            </form>
            {compareTickers.map((t, i) => (
              <span
                key={t}
                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium compare-chip-${i % COMPARE_COLORS.length}`}
              >
                {t}
                <button type="button" title={`Remove ${t}`} onClick={() => removeCompareTicker(t)} className="hover:opacity-70">
                  <X size={10} />
                </button>
              </span>
            ))}
            {compareLoading && <Loader2 size={14} className="animate-spin text-blue" />}
          </div>
        )}

        {/* Annotation input */}
        {annotationMode && pendingAnnotation && (
          <div className="flex items-center gap-2 bg-amber/10 rounded-lg px-3 py-2">
            <span className="text-xs text-amber-light">
              Note at {pendingAnnotation.date} ({currencySymbol(currency)}{pendingAnnotation.price.toFixed(2)}):
            </span>
            <input
              type="text"
              value={annotationInput}
              onChange={e => setAnnotationInput(e.target.value)}
              placeholder="Type your note..."
              className="input flex-1 px-2 py-1 text-xs rounded focus:ring-amber"
              autoFocus
            />
            <button
              type="button"
              onClick={addAnnotation}
              className="px-2 py-1 rounded bg-amber text-white text-xs font-semibold hover:bg-amber-dark"
            >
              Save
            </button>
            <button
              type="button"
              title="Cancel annotation"
              onClick={() => { setPendingAnnotation(null); setAnnotationInput('') }}
              className="p-1 text-text-muted hover:text-text-primary"
            >
              <X size={12} />
            </button>
          </div>
        )}
        {annotationMode && !pendingAnnotation && (
          <div className="text-xs text-amber-light/70 px-1">
            Click on any bar in the chart to place a note.
          </div>
        )}

        {/* Existing annotations list */}
        {annotations.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-text-muted">Notes:</span>
            {annotations.map(ann => (
              <span
                key={ann.id}
                className="group flex items-center gap-1 px-2 py-0.5 rounded bg-amber/10 text-xs text-amber-light"
                title={`${ann.date} @ ${currencySymbol(currency)}${ann.price.toFixed(2)}: ${ann.note}`}
              >
                <span className="max-w-32 truncate">{ann.note}</span>
                <span className="text-amber-light/50">({ann.date.slice(5)})</span>
                <button
                  type="button"
                  title="Delete annotation"
                  onClick={() => deleteAnnotation(ann.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-rose-light"
                >
                  <Trash2 size={10} />
                </button>
              </span>
            ))}
          </div>
        )}
        </>)}
      </div>

      {/* ── OHLCV legend (crosshair) — AgentVault mode only ────────────── */}
      {chartMode === 'agentvault' && displayBar && (
        <div className="shrink-0 px-4 py-1.5 border-b border-border flex items-center gap-4 text-xs font-mono">
          <span className="text-text-muted">{displayBar.date}</span>
          <span className="text-text-secondary">O <span className="text-text-primary">{displayBar.open.toFixed(2)}</span></span>
          <span className="text-text-secondary">H <span className="text-emerald-light">{displayBar.high.toFixed(2)}</span></span>
          <span className="text-text-secondary">L <span className="text-rose-light">{displayBar.low.toFixed(2)}</span></span>
          <span className="text-text-secondary">C <span className={displayBar.close >= displayBar.open ? 'text-emerald-light' : 'text-rose-light'}>{displayBar.close.toFixed(2)}</span></span>
          <span className="text-text-secondary">V <span className="text-text-primary">{(displayBar.volume / 1_000_000).toFixed(2)}M</span></span>
        </div>
      )}

      {/* ── Chart area ──────────────────────────────────────────────────── */}
      {/* Error banner — shown above stale chart instead of replacing it */}
      {/* Error banner — only shown for AgentVault chart mode */}
      {error && chartMode === 'agentvault' && (
        <div className="shrink-0 flex items-center gap-2 px-4 py-2 bg-rose/10 border-b border-rose/30 text-xs text-rose-light">
          <Loader2 size={12} className="shrink-0" />
          <span>{error}</span>
          {bars.length > 0 && <span className="text-rose-light/60 ml-1">(showing last known data)</span>}
        </div>
      )}

      {/* ── TradingView chart ───────────────────────────────────────────── */}
      {chartMode === 'tradingview' && (
        <div className="tradingview-widget-container h-[500px]">
          <iframe
            src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_widget&symbol=${encodeURIComponent(toTradingViewSymbol(ticker))}&interval=D&hidesidetoolbar=0&symboledit=1&saveimage=0&toolbarbg=1e293b&studies=RSI%40tv-basicstudies&theme=dark&style=1&timezone=Europe%2FLondon&locale=en`}
            title={`TradingView chart for ${ticker}`}
            className="w-full h-full border-none"
            allowTransparency
          />
        </div>
      )}

      {/* ── Unified trend strip (the "spot any trend" read) ─────────────── */}
      {chartMode === 'agentvault' && trend?.has_trend && (
        <div className="card flex items-center gap-3 mb-2 px-3 py-2 flex-wrap">
          <div className={clsx('text-sm font-semibold',
            trend.direction === 'up' ? 'text-emerald-light' : trend.direction === 'down' ? 'text-rose-light' : 'text-text-secondary')}>
            {trend.direction === 'up' ? '▲' : trend.direction === 'down' ? '▼' : '◆'} {(trend.direction ?? '').toUpperCase()}
            <span className="ml-1 font-mono">{(trend.trend_score ?? 0) >= 0 ? '+' : ''}{(trend.trend_score ?? 0).toFixed(0)}</span>
          </div>
          {/* signed strength bar */}
          <div className="relative flex-1 h-2 rounded-full bg-surface-hover overflow-hidden max-w-[260px]">
            <div className="absolute top-0 bottom-0 left-1/2 w-px bg-border-light" />
            <div
              className={clsx('absolute top-0 bottom-0', (trend.trend_score ?? 0) >= 0 ? 'bg-emerald/70 left-1/2' : 'bg-rose/70 right-1/2')}
              style={{ width: `${Math.min(50, Math.abs(trend.trend_score ?? 0) / 2)}%` }}
            />
          </div>
          <span className={clsx('text-xs px-1.5 py-0.5 rounded',
            trend.acceleration === 'building' ? 'bg-emerald/15 text-emerald-light'
              : trend.acceleration === 'fading' ? 'bg-amber/15 text-amber-light'
              : 'bg-surface-hover text-text-muted')}>
            {trend.acceleration}
          </span>
          {/* price-vs-signal divergence — tape and crowd disagreeing, an early tell */}
          {trend.divergence && (
            <span
              title={trend.divergence.note}
              className={clsx('text-xs px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5',
                trend.divergence.lean === 'bullish' ? 'bg-emerald/15 text-emerald-light' : 'bg-rose/15 text-rose-light')}
            >
              ⚠ {trend.divergence.lean} divergence
            </span>
          )}
          {/* signal-agreement ratio — % of directional signals that concur (cleaner = higher conviction) */}
          {tickerIntel?.agreement_ratio != null && (tickerIntel.directional_count ?? 0) >= 2 && (
            <span
              title={`${Math.round((tickerIntel.agreement_ratio ?? 0) * 100)}% of the ${tickerIntel.directional_count ?? 0} directional signals agree on direction. Higher = cleaner conviction; ~50% = the crowd is split.`}
              className={clsx('text-xs px-1.5 py-0.5 rounded font-medium',
                (tickerIntel.agreement_ratio ?? 0) >= 0.8 ? 'bg-emerald/15 text-emerald-light'
                  : (tickerIntel.agreement_ratio ?? 0) >= 0.65 ? 'bg-amber/15 text-amber-light'
                  : 'bg-rose/15 text-rose-light')}
            >
              {Math.round((tickerIntel.agreement_ratio ?? 0) * 100)}% agree
            </span>
          )}
          {/* dimension chips */}
          <div className="hidden md:flex items-center gap-1.5 text-[10px] text-text-muted">
            {(['structure', 'momentum', 'velocity', 'acceleration'] as const).map(dim => {
              const sc = trend.components?.[dim]?.score
              if (sc == null) return null
              return (
                <span key={dim} className="px-1 rounded bg-surface-hover/60" title={`${dim}: ${sc.toFixed(0)}`}>
                  {dim.slice(0, 3)} <span className={sc >= 0 ? 'text-emerald-light' : 'text-rose-light'}>{sc >= 0 ? '+' : ''}{sc.toFixed(0)}</span>
                </span>
              )
            })}
          </div>
          {/* signal cluster badge — a burst of signals is an early trend tell */}
          {signalDensity?.cluster && (
            <span
              className={clsx('text-xs px-1.5 py-0.5 rounded font-medium',
                signalDensity.lean === 'bullish' ? 'bg-emerald/15 text-emerald-light'
                  : signalDensity.lean === 'bearish' ? 'bg-rose/15 text-rose-light'
                  : 'bg-amber/15 text-amber-light')}
              title={`${signalDensity.last7} signals in 7d, ${signalDensity.ratio}× the baseline rate`}
            >
              🔥 signal cluster ({signalDensity.last7}, {signalDensity.lean})
            </span>
          )}
        </div>
      )}

      {/* ── 30-day signal-density heatstrip ─────────────────────────────── */}
      {chartMode === 'agentvault' && signalHeat && signalHeat.maxCount > 0 && (
        <div className="card flex items-center gap-2 mb-2 px-3 py-1.5">
          <span className="text-[10px] text-text-muted whitespace-nowrap shrink-0">30d signal density</span>
          <div className="flex items-center gap-[1px] overflow-x-auto flex-1 min-w-0">
            {signalHeat.days.map(d => {
              const intensity = signalHeat.maxCount > 0 ? d.count / signalHeat.maxCount : 0
              // Opacity floor so busy days read clearly; empty days stay faint.
              const opacity = d.count > 0 ? 0.25 + 0.75 * intensity : 1
              const tone =
                d.count === 0 ? 'bg-surface-hover'
                  : d.net > 0 ? 'bg-emerald-light'
                  : d.net < 0 ? 'bg-rose-light'
                  : 'bg-amber-light'
              const lean = d.count === 0 ? 'no signals'
                : d.net > 0 ? 'bullish' : d.net < 0 ? 'bearish' : 'neutral'
              const label = d.count === 0
                ? `${d.date} · no signals`
                : `${d.date} · ${d.count} signal${d.count === 1 ? '' : 's'} · ${lean}`
              return (
                <span
                  key={d.date}
                  title={label}
                  className={clsx('h-[14px] w-[9px] shrink-0 rounded-[1px]', tone)}
                  style={{ opacity }}
                />
              )
            })}
          </div>
          <span className="hidden sm:inline text-[9px] text-text-muted whitespace-nowrap shrink-0">← 30d ago … today →</span>
        </div>
      )}

      {/* ── AgentVault (lightweight-charts) ────────────────────────────── */}
      {chartMode === 'agentvault' && (
        <div className="relative h-[400px]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-base/80 z-10">
              <Loader2 className="w-6 h-6 animate-spin text-blue" />
              <span className="ml-2 text-text-muted text-sm">Loading {ticker}...</span>
            </div>
          )}
          {/* Subtle background-refresh overlay — doesn't block the chart */}
          {refreshing && (
            <div className="absolute top-2 right-2 flex items-center gap-1.5 bg-surface-card/80 backdrop-blur-sm rounded px-2 py-1 text-xs text-text-muted z-10">
              <Loader2 size={11} className="animate-spin" />
              refreshing...
            </div>
          )}
          <PriceChartCore
            bars={bars}
            currency={currency}
            chartType={chartType}
            showVolume={indicators.has('volume')}
            height={400}
            background="#0f1117"
            overlays={coreOverlays}
            markers={coreMarkers}
            priceLines={corePriceLines}
            timeVisible={coreTimeVisible}
            onCrosshair={setCrosshairData}
            onBarClick={handleBarClick}
          />
        </div>
      )}

      {/* ── AgentVault sub-panels (compare + indicators) ───────────────── */}
      {chartMode === 'agentvault' && <>

      {/* ── Compare chart ──────────────────────────────────────────────── */}
      {compareMode && compareData.length > 0 && compareTickerList.length > 0 && (
        <div className="px-4 py-2 border-t border-border">
          <div className="panel-title mb-1">Normalized Price Comparison (rebased to 100)</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={compareData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fill: '#6b7280', fontSize: 10 }}
                axisLine={{ stroke: '#1e2030' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#6b7280', fontSize: 10 }}
                axisLine={{ stroke: '#1e2030' }}
                tickLine={false}
                domain={['auto', 'auto']}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e2030', border: '1px solid #374151', borderRadius: 8, fontSize: 11, color: 'rgb(var(--text-primary))'}}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(value: number, name: string) => [`${value.toFixed(2)}`, name]}
              />
              <Legend
                wrapperStyle={{ fontSize: 11 }}
              />
              {compareTickerList.map((t, i) => (
                <Line
                  key={t}
                  type="monotone"
                  dataKey={`values.${t}`}
                  name={t}
                  stroke={COMPARE_COLORS[i % COMPARE_COLORS.length]}
                  dot={false}
                  strokeWidth={1.5}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Trend Ribbon — price-trend score over the lookback ──────────── */}
      {chartMode === 'agentvault' && trendSeries.length > 2 && (
        <div className="px-4 py-1 border-t border-border">
          <div className="mb-0.5 flex items-center gap-2">
            <span className="panel-title">Trend Ribbon</span>
            <span className="text-[10px] text-text-muted/70">structure + momentum, rolling — green above 0 = uptrend building</span>
          </div>
          <ResponsiveContainer width="100%" height={70}>
            <BarChart data={trendSeries} margin={{ top: 2, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 9 }} axisLine={{ stroke: '#1e2030' }} tickLine={false} minTickGap={40} />
              <YAxis domain={[-100, 100]} tick={{ fill: '#6b7280', fontSize: 9 }} axisLine={{ stroke: '#1e2030' }} tickLine={false} width={28} />
              <ReferenceLine y={0} stroke="#374151" strokeWidth={1} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e2030', border: '1px solid #374151', borderRadius: 8, fontSize: 11, color: 'rgb(var(--text-primary))' }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(value: number) => [`${value >= 0 ? '+' : ''}${value.toFixed(0)}`, 'Trend']}
              />
              <Bar dataKey="score" isAnimationActive={false}>
                {trendSeries.map((p, i) => (
                  <Cell key={i} fill={p.score >= 0 ? '#22c55e' : '#f43f5e'} fillOpacity={0.55} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Recent Signals list — what the markers actually said ────────── */}
      {chartMode === 'agentvault' && showSignals && signalEvents.length > 0 && (
        <div className="px-4 py-2 border-t border-border">
          <div className="mb-1 flex items-center gap-2">
            <span className="panel-title">Recent Signals</span>
            <span className="text-[10px] text-text-muted/70">the events plotted on the chart, newest first</span>
          </div>

          {/* by-source breakdown chips — which feeds are firing, and their lean */}
          {signalsBySource && signalsBySource.rows.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2 text-[10px]">
              <span className="text-text-muted/70 shrink-0">by source</span>
              {signalsBySource.rows.map((r) => {
                const active = sourceFilter === r.source
                return (
                  <button
                    type="button"
                    key={r.source}
                    onClick={() => setSourceFilter(active ? null : r.source)}
                    title={`${r.source}: ${r.count} signals, ${r.lean} lean — click to ${active ? 'clear' : 'filter'}`}
                    className={clsx(
                      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors',
                      active && 'ring-1 ring-inset ring-blue/60',
                      r.lean === 'bullish'
                        ? 'bg-emerald/15 text-emerald-light hover:bg-emerald/25'
                        : r.lean === 'bearish'
                          ? 'bg-rose/15 text-rose-light hover:bg-rose/25'
                          : 'bg-surface-hover text-text-secondary hover:bg-surface-hover/80',
                    )}
                  >
                    <span
                      className={clsx(
                        'w-1.5 h-1.5 rounded-full shrink-0',
                        r.lean === 'bullish'
                          ? 'bg-emerald-light'
                          : r.lean === 'bearish'
                            ? 'bg-rose-light'
                            : 'bg-text-muted',
                      )}
                    />
                    <span className="truncate max-w-[120px]">{r.source.replace(/_/g, ' ')}</span>
                    <span className="font-mono opacity-80">{r.count}</span>
                  </button>
                )
              })}
              {signalsBySource.totalSources > signalsBySource.rows.length && (
                <span className="text-text-muted/70 shrink-0">
                  +{signalsBySource.totalSources - signalsBySource.rows.length} more
                </span>
              )}
              {sourceFilter && (
                <button
                  type="button"
                  onClick={() => setSourceFilter(null)}
                  className="text-text-muted/70 hover:text-text-primary underline shrink-0"
                >
                  clear filter
                </button>
              )}
            </div>
          )}

          <div className="space-y-1 max-h-44 overflow-y-auto">
            {[...signalEvents]
              .filter(s => !sourceFilter || (s.source || 'unknown') === sourceFilter)
              .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
              .slice(0, 12)
              .map((s, i) => {
                const sent = s.sentiment ?? 0
                const bull = sent > 0.1, bear = sent < -0.1
                const d = (s.created_at || '').slice(0, 10)
                return (
                  <div key={i} className="flex items-start gap-2 text-[11px]">
                    <span className={clsx('w-1.5 h-1.5 mt-1 rounded-full shrink-0',
                      bull ? 'bg-emerald-light' : bear ? 'bg-rose-light' : 'bg-text-muted')} />
                    <span className="font-mono text-text-muted shrink-0 w-20">{d}</span>
                    <span className="text-text-secondary shrink-0 w-28 truncate">{(s.source || '').replace(/_/g, ' ')}</span>
                    <span className="text-text-muted truncate">{s.summary || s.signal_type || ''}</span>
                  </div>
                )
              })}
          </div>
        </div>
      )}

      {/* ── RSI Panel ──────────────────────────────────────────────────── */}
      {indicators.has('rsi') && rechartsData.length > 0 && (
        <div className="px-4 py-1 border-t border-border">
          <div className="panel-title mb-0.5">RSI (14)</div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={rechartsData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fill: '#6b7280', fontSize: 10 }}
                axisLine={{ stroke: '#1e2030' }}
                tickLine={false}
              />
              <YAxis
                domain={[0, 100]}
                ticks={[0, 30, 50, 70, 100]}
                tick={{ fill: '#6b7280', fontSize: 10 }}
                axisLine={{ stroke: '#1e2030' }}
                tickLine={false}
                width={30}
              />
              <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="4 4" strokeWidth={1} />
              <ReferenceLine y={30} stroke="#10b981" strokeDasharray="4 4" strokeWidth={1} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e2030', border: '1px solid #374151', borderRadius: 8, fontSize: 11, color: 'rgb(var(--text-primary))'}}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(value: number) => [value != null ? value.toFixed(2) : '-', 'RSI']}
              />
              <Line
                type="monotone"
                dataKey="rsi"
                stroke="#a78bfa"
                dot={false}
                strokeWidth={1.5}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── MACD Panel ─────────────────────────────────────────────────── */}
      {indicators.has('macd') && rechartsData.length > 0 && (
        <div className="px-4 py-1 border-t border-border">
          <div className="panel-title mb-0.5">MACD (12, 26, 9)</div>
          <ResponsiveContainer width="100%" height={100}>
            <ComposedChart data={rechartsData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fill: '#6b7280', fontSize: 10 }}
                axisLine={{ stroke: '#1e2030' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#6b7280', fontSize: 10 }}
                axisLine={{ stroke: '#1e2030' }}
                tickLine={false}
                width={40}
                domain={['auto', 'auto']}
              />
              <ReferenceLine y={0} stroke="#374151" strokeWidth={1} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e2030', border: '1px solid #374151', borderRadius: 8, fontSize: 11, color: 'rgb(var(--text-primary))'}}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(value: number, name: string) => [value != null ? value.toFixed(4) : '-', name]}
              />
              <Bar
                dataKey="macd_histogram"
                name="Histogram"
                fill="#6b7280"
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="macd"
                name="MACD"
                stroke="#3b82f6"
                dot={false}
                strokeWidth={1.5}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="macd_signal"
                name="Signal"
                stroke="#ef4444"
                dot={false}
                strokeWidth={1}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Stochastic Panel ───────────────────────────────────────────── */}
      {indicators.has('stoch') && rechartsData.length > 0 && (
        <div className="px-4 py-1 border-t border-border">
          <div className="panel-title mb-0.5">Stochastic (14, 3)</div>
          <ResponsiveContainer width="100%" height={100}>
            <LineChart data={rechartsData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fill: '#6b7280', fontSize: 10 }}
                axisLine={{ stroke: '#1e2030' }}
                tickLine={false}
              />
              <YAxis
                domain={[0, 100]}
                ticks={[0, 20, 50, 80, 100]}
                tick={{ fill: '#6b7280', fontSize: 10 }}
                axisLine={{ stroke: '#1e2030' }}
                tickLine={false}
                width={30}
              />
              <ReferenceLine y={80} stroke="#ef4444" strokeDasharray="4 4" strokeWidth={1} />
              <ReferenceLine y={20} stroke="#10b981" strokeDasharray="4 4" strokeWidth={1} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e2030', border: '1px solid #374151', borderRadius: 8, fontSize: 11, color: 'rgb(var(--text-primary))'}}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(value: number, name: string) => [value != null ? value.toFixed(2) : '-', name]}
              />
              <Line
                type="monotone"
                dataKey="stoch_k"
                name="%K"
                stroke="#3b82f6"
                dot={false}
                strokeWidth={1.5}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="stoch_d"
                name="%D"
                stroke="#f59e0b"
                dot={false}
                strokeWidth={1}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      </>}
    </div>
  )
}
